import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/_components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/_components/dialog";
import { Checkbox } from "@/components/_components/checkbox";
import { Key, Shield, CheckSquare } from "lucide-react";
import toast from "react-hot-toast";
import * as api from "@/lib/api/client";
import type { RoleDto, AuthorityDto } from "@/lib/api/types";
import { Skeleton } from "@/components/_components/skeleton";
import { useErrorHandler } from "@/hooks/useErrorHandler";

interface ManageAuthoritiesDialogProps {
  role: RoleDto;
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

const ManageAuthoritiesDialog: React.FC<ManageAuthoritiesDialogProps> = ({
  role,
  onSuccess,
  trigger,
}) => {
  const { handleError } = useErrorHandler();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [allAuthorities, setAllAuthorities] = useState<AuthorityDto[]>([]);
  const [selectedAuthorityIds, setSelectedAuthorityIds] = useState<number[]>([]);

  // Group authorities by groupName field
  const groupedAuthorities = allAuthorities.reduce((groups, authority) => {
    const group = authority.groupName || 'Other';
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(authority);
    return groups;
  }, {} as Record<string, AuthorityDto[]>);

  const fetchData = useCallback(async () => {
    if (!role.id) return;
    
    try {
      setIsLoading(true);
      const [authoritiesResponse, roleAuthoritiesResponse] = await Promise.all([
        api.getAllAuthorities(),
        api.getRoleAuthorities(role.id),
      ]);
      
      setAllAuthorities(authoritiesResponse.data || []);
      setSelectedAuthorityIds((roleAuthoritiesResponse.data || []).map(auth => auth.id));
    } catch (error: unknown) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, [role.id, handleError]);

  useEffect(() => {
    if (open && role.id) {
      fetchData();
    }
  }, [open, role.id, fetchData]);

  const handleAuthorityToggle = (authorityId: number, checked: boolean) => {
    if (checked) {
      setSelectedAuthorityIds(prev => [...prev, authorityId]);
    } else {
      setSelectedAuthorityIds(prev => prev.filter(id => id !== authorityId));
    }
  };

  const handleGroupSelectAll = (groupAuthorities: AuthorityDto[], checked: boolean) => {
    const groupIds = groupAuthorities.map(auth => auth.id);
    if (checked) {
      setSelectedAuthorityIds(prev => [...new Set([...prev, ...groupIds])]);
    } else {
      setSelectedAuthorityIds(prev => prev.filter(id => !groupIds.includes(id)));
    }
  };

  const isGroupFullySelected = (groupAuthorities: AuthorityDto[]) => {
    return groupAuthorities.every(auth => selectedAuthorityIds.includes(auth.id));
  };

  const isGroupPartiallySelected = (groupAuthorities: AuthorityDto[]) => {
    return groupAuthorities.some(auth => selectedAuthorityIds.includes(auth.id)) && 
           !isGroupFullySelected(groupAuthorities);
  };

  const handleSave = async () => {
    if (!role.id) {
      toast.error("Role ID is missing");
      return;
    }

    try {
      setIsSaving(true);
      await api.assignAuthoritiesToRole(role.id, selectedAuthorityIds);
      toast.success("Authorities updated successfully");
      setOpen(false);
      onSuccess();
    } catch (error: unknown) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSelectedAuthorityIds([]);
      setAllAuthorities([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <Key className="h-4 w-4" />
            Manage Authorities
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Manage Authorities for "{role.name}"
          </DialogTitle>
          <DialogDescription>
            Select the authorities that should be assigned to this role. Authorities are grouped by category.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="h-6 w-32" />
                  <div className="grid grid-cols-2 gap-3">
                    {[...Array(4)].map((_, j) => (
                      <div key={j} className="flex items-center space-x-3">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 flex-1" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : Object.keys(groupedAuthorities).length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No authorities available</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedAuthorities).map(([groupName, groupAuthorities]) => (
                <div key={groupName} className="space-y-3">
                  {/* Group Header with Select All */}
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/30">
                    <h3 className="font-semibold text-sm text-foreground">{groupName}</h3>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`group-${groupName}`}
                        checked={isGroupFullySelected(groupAuthorities)}
                        onCheckedChange={(checked) => 
                          handleGroupSelectAll(groupAuthorities, checked as boolean)
                        }
                        disabled={isSaving}
                        className={isGroupPartiallySelected(groupAuthorities) ? "data-[state=checked]:bg-orange-500" : ""}
                      />
                      <label 
                        htmlFor={`group-${groupName}`}
                        className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1"
                      >
                        <CheckSquare className="h-3 w-3" />
                        Select All
                      </label>
                    </div>
                  </div>

                  {/* Authorities Grid - 2 per row */}
                  <div className="grid grid-cols-2 gap-3">
                    {groupAuthorities.map((authority) => (
                      <div key={authority.id} className="flex items-start space-x-3 p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors">
                        <Checkbox
                          id={`authority-${authority.id}`}
                          checked={selectedAuthorityIds.includes(authority.id)}
                          onCheckedChange={(checked) => 
                            handleAuthorityToggle(authority.id, checked as boolean)
                          }
                          disabled={isSaving}
                        />
                        <div className="flex-1 min-w-0">
                          <label 
                            htmlFor={`authority-${authority.id}`}
                            className="text-sm font-medium cursor-pointer block"
                          >
                            {authority.name}
                          </label>
                          {authority.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {authority.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isLoading || isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManageAuthoritiesDialog;